/**
 * Incentives and Payout System - Shared TypeScript Types
 *
 * These types are shared between frontend and backend
 */

/**
 * Period lifecycle states
 */
export enum PeriodStatus {
  OPEN = 'open',
  LOCKED = 'locked',
  PAY_MONTH_1_COMPLETE = 'pay_month_1_complete',
  PAY_MONTH_2_COMPLETE = 'pay_month_2_complete',
}

/**
 * Payout status for Pay Month 1 (40%)
 */
export enum PayMonth1Status {
  SCHEDULED = 'scheduled',
  PAID = 'paid',
  WITHHELD = 'withheld',
}

/**
 * Payout status for Pay Month 2 (60%)
 */
export enum PayMonth2Status {
  PENDING_RETENTION = 'pending_retention',
  SCHEDULED = 'scheduled',
  PAID = 'paid',
  WITHHELD = 'withheld',
  DENIED = 'denied',
}

/**
 * User roles (incentives context; for app-wide role use constants/roles UserRole)
 */
export enum IncentiveUserRole {
  SDR = 'SDR',
  IM = 'IM',
  AM = 'AM',
  ADMIN = 'ADMIN',
  ADMIN_ASSISTANT = 'ADMIN_ASSISTANT',
}

/**
 * Incentive period (year-month)
 */
export interface IncentivePeriod {
  id: string;
  year: number;
  month: number;
  status: PeriodStatus;
  locked_at: string | null;
  locked_by: string | null;
  pay_month_1_completed_at: string | null;
  pay_month_1_completed_by: string | null;
  pay_month_2_completed_at: string | null;
  pay_month_2_completed_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Client incentive configuration
 */
export interface ClientIncentiveConfig {
  id: string;
  period_id: string;
  client_code: string;
  bonus_enabled: boolean;
  monthly_goal: number | null;
  bonus_pool_amount: number | null;
  sdr_split_percentage: number;
  im_split_percentage: number;
  am_split_percentage: number;
  rounding_method: string;
  version: number;
  created_at: string;
  updated_at: string;
}

/**
 * User payout record
 */
export interface UserPayout {
  id: string;
  period_id: string;
  client_code: string;
  user_id: string;
  user_role: IncentiveUserRole;
  earned_amount: number;
  pay_month_1_amount: number;
  pay_month_1_status: PayMonth1Status;
  pay_month_1_paid_at: string | null;
  pay_month_1_transaction_ref: string | null;
  pay_month_2_amount: number;
  pay_month_2_status: PayMonth2Status;
  pay_month_2_paid_at: string | null;
  pay_month_2_transaction_ref: string | null;
  retention_gate_approved: boolean;
  retention_gate_approved_by: string | null;
  retention_gate_approved_at: string | null;
  retention_denial_reason: string | null;
  is_override: boolean;
  override_reason: string | null;
  override_note: string | null;
  override_created_by: string | null;
  override_created_at: string | null;
  calculated_at: string;
  last_recalculated_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Period calculation result
 */
export interface PeriodCalculationResult {
  period_id: string;
  year: number;
  month: number;
  total_clients_calculated: number;
  total_eligible_clients: number;
  total_payouts: number;
  total_pay_month_1: number;
  total_pay_month_2: number;
  calculations: PayoutCalculation[];
  warnings: string[];
  errors: string[];
}

/**
 * Payout calculation
 */
export interface PayoutCalculation {
  user_id: string;
  client_code: string;
  period_id: string;
  user_role: IncentiveUserRole;
  qualified_count: number;
  booked_count: number;
  goal: number;
  goal_hit: boolean;
  eligible: boolean;
  earned_amount: number;
  pay_month_1_amount: number;
  pay_month_2_amount: number;
  rounding_method: string;
}

/**
 * Pending retention gate
 */
export interface PendingRetentionGate {
  payout_id: string;
  user_id: string;
  user_email: string;
  user_full_name: string;
  user_role: IncentiveUserRole;
  client_code: string;
  earned_amount: number;
  pay_month_1_amount: number;
  pay_month_2_amount: number;
  period: string;
}

/**
 * Payout override
 */
export interface PayoutOverride {
  payout_id: string;
  period_id: string;
  client_code: string;
  user_id: string;
  user_email: string;
  user_full_name: string;
  user_role: IncentiveUserRole;
  earned_amount: number;
  pay_month_1_amount: number;
  pay_month_2_amount: number;
  override_reason: string;
  override_note: string | null;
  override_created_by: string;
  override_created_by_email: string | null;
  override_created_at: string;
  calculated_at: string;
}

/**
 * Override summary
 */
export interface OverrideSummary {
  total_payouts: number;
  override_count: number;
  override_percentage: number;
  total_override_amount: number;
  overrides_by_reason: Record<string, number>;
}

/**
 * Audit log entry
 */
export interface AuditLogEntry {
  id: string;
  period_id: string | null;
  client_code: string | null;
  entity_type: string;
  entity_id: string;
  action_type: string;
  performed_by: string;
  performed_at: string;
  before_value: Record<string, any> | null;
  after_value: Record<string, any> | null;
  reason_code: string | null;
  notes: string | null;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}
